import { Signal } from 'typed-signals';
import cloneDeep from 'lodash/cloneDeep';
import { getActiveLanguage } from '@softgames/localization';
import type { PlatformPayments } from '../platform/Platform';
import type { AppStateController } from '../state/AppStateController';
import type { IAPProduct, IAPProductId } from './types';
import { IAPPlacement, iapPlacementData } from './types';
import { LoadingView } from '../../views/LoadingView';
import type { ResourceController } from '../resources/ResourceController';
import { TransactionReason } from '../resources/types';
import { PurchaseResultView } from '../../mainMenu/shopPage/PurchaseResultView';
import type { AppController } from '../AppController';
import type { TrackingModule } from '../tracking/types';
import type { Condition } from '../offers/conditionsTypes';
import type {
  OfferLocation,
  GameOfferDefinition,
  OffersGameData,
  OfferTrigger,
  StateProxy,
  GameOfferVariant,
  GameActiveOfferData,
} from '../offers/offersTypes';
import type { OfferSystemController } from '../../libs/offerSystem/OfferSystemController';
import { initOfferSystem } from '../offers/initOfferSystem';
import type { PurchasesList } from '../../libs/offerSystem/OfferSystemHistory';
import { loadRemoteOfferConfig } from './loadRemoteOfferConfig';
import type { App } from '../App';

interface IAPControllerConfig {
  app: App;
  offers: GameOfferDefinition[];
  controller: AppController;
  payments: PlatformPayments;
  resources: ResourceController;
  tracking: TrackingModule;
  state: AppStateController;
  offersUrl: string | null;
  isDev: boolean;
}

interface PlatformDataEntry {
  platformId: string;
  price: string;
  priceAmount: number;
  priceCurrencyCode: string;
}

export class IAPController {
  private offerSystem!: OfferSystemController<StateProxy, Condition, OffersGameData>;

  public readonly onOffersUpdated = new Signal<(offers: IAPProduct[]) => void>();

  public onPurchaseSuccessful = new Signal<(productId: string) => void>();

  public onInitialized = new Signal();

  private initialized = false;

  private platformCatalog: Record<string, PlatformDataEntry> = {};

  constructor(private readonly config: IAPControllerConfig) {
    this.onPurchaseSuccessful.connect((productId) => {
      this.offerSystem.markPurchase(productId);
    });
  }

  public async initialize(): Promise<void> {
    if (this.config.offersUrl) {
      const offersToOverride = await loadRemoteOfferConfig(this.config.offersUrl);
      if (offersToOverride) {
        this.config.offers = offersToOverride;
      }
    }

    this.offerSystem = initOfferSystem(this.config.app, this.config.offers);

    this.offerSystem.onOffersUpdated.connect((offers) => {
      const products = offers.map((offer) => this.activeOfferToIAPProduct(offer)).filter(Boolean) as IAPProduct[];
      this.onOffersUpdated.emit(products);
    });

    const { payments } = this.config;

    this.setupDelayedProductConsumption();

    const initSuccessful = await payments.init();

    if (!initSuccessful) return;

    const catalogSuccessful = await this.fetchPlatformCatalog();

    if (!catalogSuccessful) return;

    this.initialized = true;
    this.onInitialized.emit();
  }

  private setupDelayedProductConsumption(): void {
    const { payments, resources } = this.config;

    payments.onDelayedProductConsumed.connect(async (platformProductId) => {
      const offerData = this.findOfferByPlatformId(platformProductId);
      if (!offerData) return;

      resources.addInAppPurchase(offerData.variant.content, TransactionReason.ShopPurchase);
      this.onPurchaseSuccessful.emit(offerData.offer.id);
    });
  }

  private findOfferByPlatformId(
    platformProductId: string,
  ): { offer: GameOfferDefinition; variant: GameOfferVariant } | null {
    const offer = this.config.offers.find(({ variants }) =>
      variants.some((variant) => platformProductId === variant.platformId),
    );
    if (!offer) return null;

    const variant = offer.variants.find(({ platformId }) => platformId === platformProductId);
    if (!variant) return null;

    return { offer, variant };
  }

  private async fetchPlatformCatalog(): Promise<boolean> {
    const { payments } = this.config;
    const platformCatalog = await payments.getCatalog();

    if (!platformCatalog) return false;

    platformCatalog.forEach((platformProduct) => {
      if (!platformProduct.price) return;
      if (!platformProduct.priceCurrencyCode) return;

      this.platformCatalog[platformProduct.productID] = {
        price: platformProduct.price,
        priceAmount: platformProduct.priceAmount,
        priceCurrencyCode: platformProduct.priceCurrencyCode,
        platformId: platformProduct.productID,
      };
    });

    if (Object.values(this.platformCatalog).length === 0) return false;

    return true;
  }

  public getCatalog(location: OfferLocation, trigger: OfferTrigger | null): IAPProduct[] | null {
    if (!this.initialized) return null;

    return this.offerSystem
      .getActiveOffers(location, trigger)
      .map((activeOffer) => this.activeOfferToIAPProduct(activeOffer) ?? null)
      .filter(Boolean) as IAPProduct[];
  }

  private activeOfferToIAPProduct(activeOffer: GameActiveOfferData): IAPProduct | null {
    const { offer, variant, endDate, activationId, startDate } = activeOffer;

    const variantConfig = offer.variants[variant];
    if (!variantConfig) return null;
    const platformData = this.platformCatalog[variantConfig.platformId];
    if (!platformData) return null;

    return {
      id: offer.id,
      platformId: variantConfig.platformId,
      currency: platformData.priceCurrencyCode,
      data: { ...offer.data, ...variantConfig.data },
      tags: offer.tags,
      locations: [...offer.location],
      displayName: offer.displayName ? offer.displayName[getActiveLanguage()] : undefined,
      cornerFlash: variantConfig.badge ? variantConfig.badge[getActiveLanguage()] : undefined,
      endDate,
      price: platformData.priceAmount,
      priceLabel: platformData.price,
      rewards: variantConfig.content,
      usdCentPrice: variantConfig.iapCostCents,
      variantId: variantConfig.id,
      activationId,
      startDate,
    };
  }

  public async getCatalogAsync(location: OfferLocation, trigger: OfferTrigger | null): Promise<IAPProduct[]> {
    await this.waitForInitialized();

    return this.getCatalog(location, trigger) ?? [];
  }

  public getProduct(productId: IAPProductId): IAPProduct | null {
    if (!this.offerSystem) return null;
    const activeOffer = this.offerSystem.getActiveOffer(productId);
    if (!activeOffer) return null;
    return cloneDeep(this.activeOfferToIAPProduct(activeOffer));
  }

  public async getProductAsync(productId: IAPProductId): Promise<IAPProduct | null> {
    await this.waitForInitialized();
    return this.getProduct(productId);
  }

  public async getPurchaseHistoryAsync(): Promise<PurchasesList> {
    await this.waitForInitialized();
    return this.offerSystem.getPurchasesLists();
  }

  public getPurchaseHistorySync(): PurchasesList | null {
    if (!this.initialized) return null;
    return this.offerSystem.getPurchasesLists();
  }

  private async waitForInitialized(): Promise<void> {
    if (!this.initialized) {
      await new Promise((resolve) => {
        this.onInitialized.connect(resolve);
      });
    }
  }

  public isInitialized(): boolean {
    return this.initialized;
  }

  public isPayer(): boolean {
    if (!this.initialized) return false;
    return this.offerSystem.getPurchasesLists().length > 0;
  }

  /**
   * Get purchase history synchronously (returns empty array if not initialized)
   * Used for ad-cooldown calculations where async is not possible
   */
  public getPurchasesListsSync(): PurchasesList {
    if (!this.initialized) return [];
    return this.offerSystem.getPurchasesLists();
  }

  /**
   * Get the timestamp of the last purchase, or null if no purchases.
   * Used for payer ad-cooldown calculation.
   */
  public getLastPurchaseTimestamp(): number | null {
    try {
      const purchases = this.getPurchasesListsSync();
      if (!purchases || purchases.length === 0) return null;
      const lastPurchase = purchases.reduce((latest, purchase) =>
        purchase.date > latest.date ? purchase : latest,
      );
      return lastPurchase.date;
    } catch {
      return null;
    }
  }

  public async makePurchase(props: {
    product: IAPProduct;
    placement: IAPPlacement;
    showResults: boolean;
    offerImpressionId: string;
    // TODO temporary hack, we need to get those IAP placements cleaned up
    storeEntryPoint?: string;
  }): Promise<boolean> {
    const {
      product,
      placement,
      showResults,
      offerImpressionId,
      storeEntryPoint = iapPlacementData[placement].storeEntryPoint,
    } = props;

    void LoadingView.getInstance().show();

    const { payments, resources, tracking } = this.config;

    const purchase = await payments.makePurchase(product.platformId);

    const success = purchase.error === null;

    if (success) {
      const reason =
        placement === IAPPlacement.LevelEnd ? TransactionReason.LevelFailPopupPurchase : TransactionReason.ShopPurchase;
      resources.addInAppPurchase(product.rewards, reason, purchase?.paymentID);

      this.onPurchaseSuccessful.emit(product.id);
      this.config.state.setPurchasedProducts([product.id]);
    }

    void LoadingView.getInstance().hide();

    if (showResults) {
      const { overlay } = this.config.controller;
      overlay.view.content.addChild(new PurchaseResultView(product, success));
    }

    const paymentProvider = this.config.payments.getPaymentProvider();
    
    tracking.trackPurchase(
      {
      data: this.config.state.getData(),
      product,
      purchase,
      placementData: {
        ...iapPlacementData[placement],
        storeEntryPoint,
      },
      offerImpressionId,
      paymentProvider,
    });

    return success;
  }
}
